// Tulane University: public college page (#2652, round 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Tulane's Common Data Set 2025-2026 (Office of Assessment and
//    Institutional Research): C1 applicants/admits/enrollees for Fall 2025
//    with the residency table (its "unknown" column is 0), C8 test policy for
//    Fall 2027, C21/C22 plans with one early decision count for ED I and II
//    together. Quirk: OAIR hosts the PDF on Box; the source link is OAIR's
//    Common Data Set page that links to it. The CDS gives 2025-2026 costs
//    only (it marks 2026-2027 as not yet available).
//  - Tulane Undergraduate Admission: Deadlines & Forms, Application
//    Instructions (the four timelines and what Early Decision commits you
//    to), Standardized Tests ("Standardized Testing Update for Fall 2027"),
//    Portfolios & Auditions. The deadlines pages carry no year; the testing
//    page names Fall 2027, and they are the live pages for this cycle.
//  - Tulane Admission, Cost of Attendance: the 2026-2027 budget for a
//    first-year student living on campus in Newcomb-Tulane College.
//    Transportation "varies" and is not in the $98,710 total.
//  - College Scorecard API, school id 160755 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Tulane Common Data Set 2025-2026',
        url: 'https://oair.tulane.edu/common-data-set',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Tulane Admission, Deadlines & Forms',
        url: 'https://admission.tulane.edu/apply/deadlines-forms',
        read: '2026-10-01',
    },
    instructions: {
        label: 'Tulane Admission, Application Instructions',
        url: 'https://admission.tulane.edu/apply/instructions',
        read: '2026-10-01',
    },
    testing: {
        label: 'Tulane Admission, Standardized Tests',
        url: 'https://admission.tulane.edu/apply/instructions/standardized-tests',
        read: '2026-10-01',
    },
    portfolios: {
        label: 'Tulane Admission, Portfolios & Auditions',
        url: 'https://admission.tulane.edu/apply/portfolios-auditions',
        read: '2026-10-01',
    },
    cost: {
        label: 'Tulane Admission, Cost of Attendance',
        url: 'https://admission.tulane.edu/tuition-aid/cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?160755-Tulane-University-of-Louisiana',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'tulane-university',
    kbId: 'tulane_university',
    name: 'Tulane University',
    shortName: 'Tulane',
    location: 'New Orleans, LA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Tulane athletics style guide); decoration only.
    branding: {
        primary: '#006747',
        secondary: '#418FDE',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/tulane_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 32942,
        admitted: 4763,
        enrolled: 1858,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2676, admitted: 595 },
            { group: 'Out-of-state', applied: 25512, admitted: 3593 },
            { group: 'International', applied: 4754, admitted: 575 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Tulane\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set gives one count for Early Decision I and II together. Tulane\'s Early Action plan is not restrictive, and the Common Data Set gives no early action counts.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II', applied: 2077, admitted: 1209 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'No audition or portfolio is required for admission to Tulane. A few things ask for more:',
        rows: [
            { school: 'Architecture', requirement: 'A portfolio is highly recommended: up to 10 pieces showing the breadth of your design experience, including a couple of freehand drawings, uploaded to the Optional Portfolio section of your Green Wave Portal.', source: 'portfolios' },
            { school: 'Music, studio art, theatre and dance', requirement: 'Optional portfolios follow set guidelines for each area (for music: Jazz Studies, Composition, Instrumental or Vocal Performance). Add them as soon as you can after applying; the committee will not wait for them.', source: 'portfolios' },
            { school: 'Altman Program in International Studies & Business', requirement: 'A separate application: early deadline February 15, regular deadline March 10.', source: 'deadlines' },
        ],
        source: 'portfolios',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December 15', binding: true },
            { plan: 'Early Action', deadline: 'November 10', decision: 'By January 10', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'April 1', binding: false },
        ],
        note: 'The Common App opens August 1, and deadlines are postmark dates. Enrollment deposits are due January 15 for Early Decision, March 1 for Early Decision II and May 1 for Early Action and Regular Decision. The CSS Profile and FAFSA are due November 15 (Early Decision), December 15 (Early Action), January 15 (Early Decision II) and February 15 (Regular Decision). Deferred Early Action applicants hear by April 1. Tulane asks for a counselor recommendation, not teacher recommendations. If you applied Early Action or Regular Decision, you can switch to Early Decision II in your Green Wave Portal.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding. You, a parent and your counselor sign the Early Decision Agreement. If you are admitted with a financial aid offer that meets your demonstrated need (when you applied for aid on time), you pay a $1,000 non-refundable deposit and withdraw all other applications. You can decline the offer if Tulane has not met your calculated demonstrated need.',
        bindingSource: 'instructions',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending SAT or ACT scores stays optional for fall 2027. Tulane superscores both tests and generally encourages students with a 1300 or higher on the SAT or a 28 on the ACT to send scores. You can self-report scores; if you are admitted and enroll, official scores are due within three weeks of your enrollment deposit.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // Announced for this cycle (fall 2027 entry) and already in effect.
    currentChanges: [
        { topic: 'Pathways to Medicine', line: 'The SAT or ACT is now required for Pathways to Medicine, the invitation-only program that admits students to Newcomb-Tulane College and the Tulane School of Medicine at once.', source: 'testing' },
        { topic: 'Merit scholarships', line: 'SAT or ACT scores, AP exams, IB results, A levels and other exams are not required, but they are now a preferred credential for academic merit scholarships. Testing plays no part in need-based aid or in artistic, athletic or service scholarships.', source: 'testing' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 70622 },
            { item: 'Mandatory fees', amount: 4514 },
            { item: 'Housing (weighted average, first-year options)', amount: 11758 },
            { item: 'Meals (minimum required plan)', amount: 8780 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Miscellaneous', amount: 1778 },
            { item: 'Loan fees', amount: 58 },
            { item: 'Transportation', amount: null },
        ],
        total: 98710,
        note: 'For a first-year student living on campus in Newcomb-Tulane College. Transportation varies, so it is not in the $98,710 total. Miscellaneous covers expenses such as the one-time $300 first-year orientation fee, health insurance if you are not covered, and laundry.',
        source: 'cost',
        netPrice: {
            average: 40693,
            byIncome: [
                { income: '$0 to $30,000', amount: 23823 },
                { income: '$30,001 to $48,000', amount: 27524 },
                { income: '$48,001 to $75,000', amount: 27708 },
                { income: '$75,001 to $110,000', amount: 30737 },
                { income: 'Over $110,000', amount: 59766 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Tulane\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
